import React from "react";
import { createPortal } from "react-dom";

/** Open state for a trigger + floating panel. The panel renders in a
 * portal with fixed positioning, so table and modal overflow never clip it;
 * it opens upward near the bottom of the viewport. Closes on an outside
 * press or Escape (before any dialog underneath sees the key) and hands
 * focus back to the trigger. No scrim, so the page stays clickable. */
export function usePopover<T extends HTMLElement>(
  align: "start" | "end" = "start",
) {
  const [open, setOpen] = React.useState(false);
  const [style, setStyle] = React.useState<React.CSSProperties>({});
  const root = React.useRef<HTMLDivElement>(null);
  const panel = React.useRef<HTMLElement | null>(null);
  const trigger = React.useRef<T>(null);

  const close = React.useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) trigger.current?.focus();
  }, []);

  const place = React.useCallback(() => {
    const r = trigger.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.max(panel.current?.offsetWidth ?? 200, r.width);
    const below = window.innerHeight - r.bottom - 12;
    const up = below < 240 && r.top > below;
    setStyle({
      position: "fixed",
      minWidth: r.width,
      maxHeight: Math.min(320, (up ? r.top : below) - 8),
      ...(up
        ? { bottom: window.innerHeight - r.top + 4 }
        : { top: r.bottom + 4 }),
      ...(align === "end" || r.left + width > window.innerWidth - 8
        ? { right: Math.max(8, window.innerWidth - r.right) }
        : { left: Math.max(8, r.left) }),
    });
  }, [align]);

  // Once the panel exists its real width decides which edge it hugs.
  React.useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  const toggle = React.useCallback(() => {
    place();
    setOpen((was) => !was);
  }, [place]);

  React.useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (!root.current?.contains(t) && !panel.current?.contains(t))
        close(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
      }
    }
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, close, place]);

  return { open, toggle, close, style, root, panel, trigger };
}

/** Renders a popover panel at the end of <body>. */
export function Floating({ children }: { children: React.ReactNode }) {
  return createPortal(children, document.body);
}

/** Arrow / Home / End movement over a list of ``n`` items, plus type-ahead
 * against ``labels``. Returns the next index or null if the key isn't one. */
export function listKey(
  key: string,
  at: number,
  labels: string[],
  typed: React.MutableRefObject<{ text: string; at: number }>,
): number | null {
  const n = labels.length;
  if (!n) return null;
  if (key === "ArrowDown") return (at + 1) % n;
  if (key === "ArrowUp") return (at - 1 + n) % n;
  if (key === "Home") return 0;
  if (key === "End") return n - 1;
  if (key.length === 1 && /\S/.test(key)) {
    const now = Date.now();
    typed.current.text =
      (now - typed.current.at < 700 ? typed.current.text : "") +
      key.toLowerCase();
    typed.current.at = now;
    const find = (t: string) =>
      labels.findIndex((l) => l.toLowerCase().startsWith(t));
    let hit = find(typed.current.text);
    if (hit < 0) {
      typed.current.text = key.toLowerCase();
      hit = find(typed.current.text);
    }
    return hit >= 0 ? hit : null;
  }
  return null;
}
